import React, { useState, useEffect, useRef } from 'react';
import ReactDom from "react-dom";
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";

import { EditorContext } from '../../index';

import { Animation, AnimationProperty } from 'components/Editor/components/animations';
import { actionGroup, toggleEditMode, updateWidgetProperty } from 'actions/editor';
import { getBoundingRectForWidget } from 'components/Editor/helper/layoutManager';
import { associateFontsWithWigdet } from 'actions/fonts';
import ManageMenu from './manageMenu';
import { Property } from '../../components/properties';
import { Font } from '../../components/properties/fonts';
import { MENU } from 'constants/widgetTypes';
import { useChildrenWidgetSelected, useWidgetSelected } from 'components/Editor/hooks/useEditorSession';

import { Tab, Tabs } from "react-bootstrap";

import { Widgets } from '@paperflite-marketing/cleverstory-commons';
import { getColorsConfig } from 'services/cssHelper.service';
import Tooltip from 'components/Tooltip';

import styles from './menu.module.scss';

const MenuDraggableContainerView = function MenuDraggableContainerWidget({ style, widgetProps, widgetRef, children, parentRef, stretched, parentProps }) {
  const ref = widgetRef;

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const dispatch = useDispatch();

  const [state, setState] = useState({
    active: 'regular',
    menuStyles: {},
  });

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const properties = _.get(widgetProps, 'properties', {});
  const deviceProperties = _.get(properties, `${breakpoint}`, {});

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const parent = _.get(widgetProps, 'parent');
  const layout = _.get(widgetProps, `properties.${breakpoint}.layout`);
  const content = _.get(widgetProps, 'content');
  const trayAlignment = _.get(widgetProps, `properties.${breakpoint}.trayAlignment.position`);
  const parentRectLayout = getBoundingRectForWidget(editor, parentProps);
  const { parentLayout } = useSelector((state) => {
    const widgets = _.get(state, `${editor}.present.widgets`, []) || [];
    const parentWidget = widgets.find((widget) => widget.uid === parent);
    const parentLayout = _.get(parentWidget, `properties.${breakpoint}.layout`);
    return { parentLayout };
  }, shallowEqual);

  const defaultContent = useRef({
    content,
    stretched,
    layoutHeight: layout?.height
  });

  const editMode = useSelector(state => {
    return _.get(state, `${editor}.present.editMode`) === id
  });

  const selected = useWidgetSelected(editor, id);
  const parentSelected = useWidgetSelected(editor, parent);
  const siblingSelection = useChildrenWidgetSelected(editor, parent);
  const childrenSelection = useChildrenWidgetSelected(editor, id);

  var iconStyle = getIconStyles(properties);

  useEffect(() => {
    var menuWidgetStyles = getStyles(properties, breakpoint, 'regular');
    setState(state => {
      return {
        ...state,
        menuStyles: menuWidgetStyles,
      }
    })
  }, [state.active, deviceProperties, breakpoint]);

  // move this outside the component or at least outside the effect
  function adjustTranslatePosition(rect = {}, parentRect = {}) {
    const childWidth = rect.width || 0;
    const childHeight = rect.height || 0;
    const parentWidth = parentRect.width || 0;
    const parentHeight = parentRect.height || 0;
  
    const childX = rect.x || 0;
    const parentX = parentRect.x || 0;
  
    // X (same logic as you originally had)
    let x = parseFloat(layout.transform?.translate?.split(',')[0]) || 0;
    if (childX + childWidth > parentX + parentWidth) {
      const balanceX = (childX + childWidth) - (parentX + parentWidth);
      x = x - balanceX;
    }
    x = Math.max(0, x);
  
    // Y (now handled the same way)
    const childY = rect.y || 0;
    const parentY = parentRect.y || 0;
  
    let y = parseFloat(layout.transform?.translate?.split(',')[1]) || 0;
    if (childY + childHeight > parentY + parentHeight) {
      const balanceY = (childY + childHeight) - (parentY + parentHeight);
      y = y - balanceY;
    }
  
    return { x, y };
  }
  
  const updateWidgetPosition = () => {
    const ele = ref.current;
    const parentElement = document.getElementById(parent);

    const parentRect = parentElement.getBoundingClientRect();
    const rect = ele.getBoundingClientRect();

    let { x, y } = adjustTranslatePosition(rect, parentRect);

    if(!stretched) {
      x = x <= 0 ? 0 : x > 1200 - rect.width ? 1200 - rect.width : x;
    } else {
      const maxX = window.innerWidth - 22 - rect.width;
      x = x <= 0 ? parseFloat(layout.transform?.translate?.split(',')?.[0] || 0) || 0 : x > maxX ? maxX : x;
    }

    const newTranslate = `${x}px, ${Math.max(0, y)}px`;

    actionGroup.start();

    dispatch(
      updateWidgetProperty(editor, id, breakpoint, {
        layout: {
          ...layout,
          transform: {
            ...layout?.transform,
            translate: newTranslate,
          },
        },
      })
    );

    dispatch(
      updateWidgetProperty(editor, parent, breakpoint, {
        layout: {
          ...(parentLayout || {}),
          height: `${Math.max(parentRect?.height || 0, rect.height || 0)}px`
        },
      })
    );

    actionGroup.end();
  }

  useEffect(() => {
    setTimeout(() => {
      if (!_.isEqual(defaultContent.current.content, content)) {
        updateWidgetPosition();
        defaultContent.current.content = content;
      }
  
      if (!_.isEqual(defaultContent.current.layoutHeight, layout.height)) {
        updateWidgetPosition();
        defaultContent.current.layoutHeight = layout.height;
      }
  
      if (!_.isEqual(_.get(defaultContent, 'current.stretched'), stretched)) {
        updateWidgetPosition();
        defaultContent.current.stretched = stretched;
      }
    }, 0);
  }, [ref, parent, content, layout?.height, stretched]);

  return (
    <>
      <div
        id={id}
        ref={ref}
        style={style}
        className={cx(styles.menuButtonContainer, 'widget', {
          [styles.selected]: selected,
          [styles.parentSelected]: parentSelected,
          [styles.partialSelection]: childrenSelection.selected,
          [styles.menuStrected]: stretched
        })}
        data-widget-id={id}
        data-widget-type={type}
        data-selectable={parentSelected || siblingSelection.selected}
      >
        {breakpoint === 'mobile' &&
            <div className={styles.animationWrapper}>
            <Animation widget={widgetProps}>
              <div style={{ height: layout.height }} className={styles.wrapper}>
                <div style={iconStyle} className={styles.hamburger}>
                  <i style={{ color: _.get(state.menuStyles, 'color') }} className={cx({ ['cleverstory-menu']: !editMode }, { ['cleverstory-close']: editMode }, styles.menuIcon)} />
                </div>
              </div>
            </Animation>
            </div>
        }
        {breakpoint === 'desktop' &&
          <div className={styles.menuButtonsWrapper}>
            <Widgets.MenuButton
              menuContent={content}
              active='regular'
              breakpoint={breakpoint}
              widgetProps={widgetProps}
            />
          </div>
        }
      </div>
      {(breakpoint === 'mobile' && parentRef.current) && editMode &&
        ReactDom.createPortal(
          <div style={{ top: `${parentRectLayout.height}px` }} className={cx(
            styles.mobileMenuWrapper,
            { [styles.alignmentLeft]: trayAlignment === 'left' },
            { [styles.alignmentRight]: trayAlignment === 'right' },
          )}
          >
            <Widgets.MenuButton
              menuContent={content}
              active='regular'
              breakpoint={breakpoint}
              widgetProps={widgetProps}
            />
          </div>
          ,
          parentRef.current
        )
      }
    </>
  )
}

const MenuDraggableContainerWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState('customFields');

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => changeView('menu')}>Manage field</Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} onClick={() => changeView('style')} tooltip="Style">
        <i className={cx("cleverstory-customize")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} onClick={() => changeView('spacing')} tooltip="Spacing & Padding">
        <i className={cx("cleverstory-spacing", styles.spacingIcon)} />
      </Property.Action>

      {!editMode && view === 'menu' &&
        <ManageMenu
          id={id}
          editor={editorSession}
          editorContext={editorContext}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'style' &&
        <MenuContainerStyleEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'spacing' &&
        <MenuSpacing
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

    </>
  );
}

const MenuDraggableContainerWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  const handleEditMode = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, true));
    setView(view);
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => handleEditMode('menu')}>Expand Menu</Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} tooltip="Style" onClick={() => changeView('style')}>
        <i className={cx("cleverstory-customize")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip="Animation" onClick={() => changeView('animation')}>
        <i className={cx("cleverstory-animations")} />
      </Property.Action>

      {editMode && view === 'menu' &&
        <MenuLayoutCustomization
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'style' &&
        <MenuContainerStyleEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

    </>
  );
}

const MenuSpacing = ({ id, widget, editor, onCancel }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const deviceProperties = _.get(widget, `properties.${breakpoint}`, {});

  const menuSpacing = _.get(deviceProperties, 'padding');
  const horizontalPadding = _.get(deviceProperties, 'itemHorizontalPadding');
  const verticalPadding = _.get(deviceProperties, 'itemVerticalPadding')

  const handleSpacingChange = (type, value) => {
    var request = {
      ...deviceProperties,
      [type]: Number(value)
    }
    dispatch(updateWidgetProperty(editor, id, 'desktop', request));
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Spacing & Padding'}
      onCancel={onCancel}
    >
      <Property.Body>
        <Property name="Spacing Between Items" nameStyle={styles.propertyName} className={styles.menuSpacingProperty}>
          <Property.Slider
            input
            min={0}
            className={styles.customSlider}
            max={20}
            step={1}
            value={menuSpacing}
            onChange={(data) => handleSpacingChange('padding', data)}
          />
        </Property>
        <Property name="Items Padding" nameStyle={styles.propertyName} className={styles.menuSpacingProperty}>
          <span className={styles.subHeading}>Horizontal</span>
          <Property.Slider
            input
            min={8}
            className={styles.customSlider}
            max={50}
            step={1}
            value={horizontalPadding}
            onChange={(data) => handleSpacingChange('itemHorizontalPadding', data)}
          />
        </Property>
        <Property name="" className={styles.menuSpacingProperty}>
          <span className={styles.subHeading}>Vertical</span>
          <Property.Slider
            input
            min={8}
            className={styles.customSlider}
            max={50}
            step={1}
            value={verticalPadding}
            onChange={(data) => handleSpacingChange('itemVerticalPadding', data)}
          />
        </Property>
      </Property.Body>
    </Property.Dialog>
  )
}

const MenuLayoutCustomization = ({ id, widget, editor, onCancel }) => {
  const dispatch = useDispatch();

  const alignmentOptions = ['left', 'center', 'right'];
  const deviceProperties = _.get(widget, 'properties.mobile');
  const trayAlignment = _.get(deviceProperties, 'trayAlignment.position');
  const itemsAlignment = _.get(deviceProperties, 'alignment');

  const backgroundColor = _.get(widget, 'properties.mobile.trayBackground')

  const handleTrayAlignmentChange = (value) => {
    var request = {
      ...deviceProperties,
      trayAlignment: { position: value }
    }
    dispatch(updateWidgetProperty(editor, id, 'mobile', request));
  }

  const handleAlignmentChange = (value) => {
    var request = {
      ...deviceProperties,
      alignment: {
        ...itemsAlignment,
        ...value
      }
    }

    dispatch(updateWidgetProperty(editor, id, 'mobile', request));
  }

  const handleBackgroundColorChange = React.useCallback((color) => {
    var request = {
      ...deviceProperties,
      trayBackground: color
    }

    dispatch(updateWidgetProperty(editor, id, 'mobile', request));
  }, [deviceProperties]);

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Menu Alignment'}
      onCancel={onCancel}
    >
      <Property.Body>
        <Property name={"Menu Tray"} className={styles.buttonDesignProperty}>
          <div className={styles.menuAlignmentProperty}>
            {
              alignmentOptions.map((alignment, index) => (
                <div key={index} onClick={() => handleTrayAlignmentChange(alignment)} className={cx(styles.alignmentButton, { [styles.left]: alignment === 'left' }, { [styles.right]: alignment === 'right' })}>
                  <div className={cx(styles.modeType, { [styles.active]: trayAlignment === alignment })} />
                </div>
              ))
            }
          </div>
        </Property>
        <Property name="Items Alignment" className={styles.buttonAlignmentProperty}>
          <ButtonAlignment
            value={itemsAlignment}
            onChange={(value) => handleAlignmentChange(value, 'alignment')}
          />
        </Property>
        <Property name="">
          <Property.Color title={'Background Color'} clearable={true} value={backgroundColor} onChange={handleBackgroundColorChange} />
        </Property>
      </Property.Body>
    </Property.Dialog>
  )
}

const MenuContainerStyleEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const menuIcon = breakpoint === 'mobile';

  const properties = _.get(widget, `properties.${breakpoint}`, {});

  const alignment = _.get(properties, 'alignment', {
    position: 'center',
    margin: 5
  });

  const [state, setState] = useState({
    activeTab: 'regular'
  })

  const handleTabChange = (key) => {
    setState(() => {
      return {
        ...state,
        activeTab: key
      }
    })
  }

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleAlignmentChange = (value) => {
    const position = _.get(value, 'position');

    if (position === 'center') {
      value = {
        ...value,
        margin: 0
      }
    }

    var request = {
      ...properties,
      alignment: {
        ...alignment,
        ...value
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={menuIcon ? 'Customize Icon' : 'Customize Menu'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        {!menuIcon &&
          <Property name="Text Alignment" nameStyle={styles.propertyName} className={styles.buttonAlignmentProperty}>
            <ButtonAlignment
              value={alignment}
              onChange={handleAlignmentChange}
            />
          </Property>
        }
        <Property name={menuIcon ? 'Menu Icon Design' : "Main Menu Design"} nameStyle={styles.propertyName} className={styles.buttonDesignProperty}>
          <Tabs activeKey={state.activeTab}
            onSelect={handleTabChange}
            className={styles.buttonStyleTabs}>
            <Tab eventKey="regular" title="Regular" className={styles.buttonStyleTab}>
              <FontEditor
                editorSession={editorSession}
                id={id}
                properties={properties}
                active={'regular'}
              />
              {!menuIcon &&
                <SubMenuCustomize
                  editorSession={editorSession}
                  id={id}
                  properties={properties}
                  active={'regular'}
                />}
            </Tab>
            <Tab eventKey="hover" title="Hover" className={styles.buttonStyleTab}>
              <FontEditor
                editorSession={editorSession}
                id={id}
                properties={properties}
                active={'hover'}
              />
              {!menuIcon &&
                <SubMenuCustomize
                  editorSession={editorSession}
                  id={id}
                  properties={properties}
                  active={'hover'}
                />}
            </Tab>
          </Tabs>
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

const SubMenuCustomize = ({ editorSession, id, properties, active }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const deviceProperties = _.get(properties, `${active}`, {});

  const textColor = _.get(deviceProperties, 'dropdownColor', null);
  const backgroundColor = _.get(deviceProperties, 'dropdownBackground', null);

  const border = _.get(deviceProperties, 'dropdownBorder', {});
  const borderColor = _.get(border, 'color', null);
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleTextColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      [active]: {
        ...properties[active],
        dropdownColor: color
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBackgroundColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      [active]: {
        ...properties[active],
        dropdownBackground: color
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        dropdownBorder: {
          ...border,
          color: color
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        dropdownBorder: {
          ...border,
          width: value
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        dropdownBorder: {
          ...border,
          radius: radius
        }
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property className={styles.subMenuProperty}>
      <span className={styles.subHeading}>{`Sub Menu(Dropdown) Background`}</span>
      <Property.Accordion title={'Color'} className={styles.colorProperty}>
        <Property.Color title={'Text'} value={textColor} onChange={handleTextColorChange} />
        <Property.Color title={'Background'} gradient={true} clearable={true} value={backgroundColor} onChange={handleBackgroundColorChange} />
      </Property.Accordion>
      <Property.Accordion title={'Border'} className={styles.borderProperty}>
        <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColorChange} />
        <>
          <Property name="Stroke Width" className={styles.buttonStyleProperty}>
            <Property.Slider
              min={0}
              max={10}
              step={1}
              input
              value={borderWidth}
              onChange={handleBorderWidthChange}
            />
          </Property>
          <Property name="Corner Radius" className={styles.buttonStyleProperty}>
            <Property.BorderRadius
              value={borderRadius}
              onChange={handleBorderRadiusChange}
            />
          </Property>
        </>
      </Property.Accordion>
    </Property>
  )
}

const FontEditor = ({ editorSession, id, properties, active }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const menuIcon = breakpoint === 'mobile';
  const activeStyles = _.get(properties, `${active}`, {});
  const deviceProperties = _.get(properties, `${active}.menuStyles`, {});

  const textColor = _.get(deviceProperties, 'font.color', null);
  const backgroundColor = _.get(deviceProperties, 'background', null);

  const border = _.get(deviceProperties, 'border', {});
  const borderColor = _.get(border, 'color', null);
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const fontFamily = _.get(deviceProperties, 'font.fontFamily', 'Montserrat,sans-serif') || 'Montserrat,sans-serif';
  const fontSize = _.get(deviceProperties, 'font.fontSize') || 16;
  const lineSpacing = _.get(deviceProperties, 'font.lineSpacing') || 1.2;
  const letterSpacing = _.get(deviceProperties, 'font.letterSpacing') || 0;
  const fontStyle = _.get(deviceProperties, 'font.style', {});

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleTextColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        menuStyles: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            color: color
          }
        }
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBackgroundColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        menuStyles: {
          ...deviceProperties,
          background: color
        }
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        menuStyles: {
          ...deviceProperties,
          border: {
            ...border,
            color: color
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        menuStyles: {
          ...deviceProperties,
          border: {
            ...border,
            width: value
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        menuStyles: {
          ...deviceProperties,
          border: {
            ...border,
            radius: radius
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleFontFamily = (value) => {
    var font = _.get(value, 'font.id');
    var family = _.get(value, 'font.fontFamily');
    if (!family) {
      return;
    }

    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        menuStyles: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            fontFamily: family
          }
        }
      }
    }

    handlePropertyChange(request);
    if (font) {
      dispatch(associateFontsWithWigdet(editorSession, id, [font]));
    }
  }

  const handleSizeChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        menuStyles: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            fontSize: value
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleLetterSpacingChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        menuStyles: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            letterSpacing: value
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleFontStyleChange = (style) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        menuStyles: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            style: {
              ...fontStyle,
              [style]: !fontStyle[style]
            }
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  return (
    <>
      <Property.Accordion title={'Color'} className={styles.colorProperty}>
        <Property.Color title={breakpoint === 'desktop' ? 'Text' : 'Icon'} value={textColor} onChange={handleTextColorChange} />
        <Property.Color title={'Background'} gradient={true} clearable={true} value={backgroundColor} onChange={handleBackgroundColorChange} />
      </Property.Accordion>

      <Property.Accordion title={'Border'} className={styles.borderProperty}>
        <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColorChange} />
        <Property name="Stroke Width" className={styles.buttonStyleProperty}>
          <Property.Slider
            min={0}
            max={10}
            step={1}
            input
            value={borderWidth}
            onChange={handleBorderWidthChange}
          />
        </Property>
        <Property name="Corner Radius" className={styles.buttonStyleProperty}>
          <Property.BorderRadius
            value={borderRadius}
            onChange={handleBorderRadiusChange}
          />
        </Property>

      </Property.Accordion>
      {active === 'regular' && !menuIcon &&
        <Property.Accordion title={'Text'} className={styles.textProperty}>
          <Font.Family
            value={fontFamily}
            onChange={handleFontFamily}
            className={styles.buttonStyleProperty}
          />
          <Font.Size
            value={fontSize}
            min={8}
            max={37}
            onChange={handleSizeChange}
            className={styles.buttonStyleProperty}
          />

          <Property className={styles.buttonStyleProperty}>
            <div className={styles.fontActions}>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'bold', false) })}
                onClick={handleFontStyleChange.bind(this, 'bold')}
                tooltip="Bold"
              >
                <i className={'cleverstory-bold'} />
              </Property.Action>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'italic', false) })}
                onClick={handleFontStyleChange.bind(this, 'italic')}
                tooltip="Italic"
              >
                <i className={'cleverstory-italics'} />
              </Property.Action>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'underline', false) })}
                onClick={handleFontStyleChange.bind(this, 'underline')}
                tooltip="Underline"
              >
                <i className={'cleverstory-underline'} />
              </Property.Action>
            </div>
          </Property>
          <Property name="Character Spacing" className={styles.characterSpacingProperty}>
            <Property.Slider
              input
              min={-0.1}
              max={0.7}
              step={0.05}
              value={letterSpacing}
              onChange={handleLetterSpacingChange}>
            </Property.Slider>
          </Property>
        </Property.Accordion>
      }
    </>
  )
}

const ButtonAlignment = ({ value, onChange }) => {
  const position = _.get(value, 'position') || 'center';
  const margin = _.get(value, 'margin', 0) || 0;

  const handleAlignmentChange = (position) => {
    var request = {
      position: position,
      margin: margin
    }

    onChange(request);
  }

  const handleMarginChange = (margin) => {
    var request = {
      margin: margin
    }

    onChange(request);
  }

  return (
    <div className={styles.buttonAlignment}>
      <div className={styles.options}>
        <Tooltip placement="top" content="Left Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'left'
            })}
            onClick={handleAlignmentChange.bind(this, 'left')}
          >
            <i className={cx('cleverstory-align-left')}></i>
          </button>
        </Tooltip>
        <Tooltip placement="top" content="Center Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'center'
            })}
            onClick={handleAlignmentChange.bind(this, 'center')}
          >
            <i className={cx('cleverstory-align-center')}></i>
          </button>
        </Tooltip>
        <Tooltip placement="top" content="Right Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'right'
            })}
            onClick={handleAlignmentChange.bind(this, 'right')}
          >
            <i className={cx('cleverstory-align-right')}></i>
          </button>
        </Tooltip>
      </div>

      {position !== 'center' &&
        <Property name="Margin" className={styles.marginProperty}>
          <Property.Slider
            min={0}
            input
            max={100}
            step={1}
            value={margin}
            onChange={handleMarginChange}
          />
        </Property>
      }
    </div>
  )
}

const MenuDraggableContainerWidget = React.forwardRef((props, ref) => {
  return <MenuDraggableContainerView {...props} ref={ref} />
});

MenuDraggableContainerWidget.actions = (editor, breakpoint, props) => {

  return (
    <>
      {breakpoint === 'desktop' &&
        <MenuDraggableContainerWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }

      {breakpoint === 'mobile' &&
        <MenuDraggableContainerWidgetMobileAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

MenuDraggableContainerWidget.moveableConfig = (editorSession, widget, breakpoint) => {
  const widgetElement = document.querySelector(`#${editorSession} .editor_widget_renderer #${widget.uid}`);
  const menuSectionElement = widgetElement?.closest(`div[data-widget-type="${MENU}"]`);
  const pageElement = widgetElement?.closest(`.editor_widget_scroll_container`);

  const rect = menuSectionElement?.getBoundingClientRect();
  const parentRect = pageElement?.getBoundingClientRect();

  const left = rect?.left - parentRect?.left;
  const top = pageElement?.scrollTop + rect?.top - parentRect?.top;

  const bounds = {
    left: left,
    right: left + rect?.width,
    top: top,
    bottom: top + rect?.height
  }

  return {
    draggable: true,
    resizable: true,
    rotatable: false,
    selectable: true,
    cloneable: false,
    deletable: false,
    renderDirections: ['e','w'],
    bounds: bounds,
    onDrag: (layout, widget) => {

      const frame = new Frame(layout);
      const translate = frame.get("transform", "translate") || _.get(layout, "transform.translate", "");
      const vals = _.split(translate, ",");

      // robust parse with fallback to current DOM position
      let x = parseFloat(vals[0]);
      let y = Math.max(0, parseFloat(vals[1]));

      const newTranslate = `${x}px, ${y}px`;

      frame.set("transform", "translate", newTranslate);
    
      return frame.get();
    },
    onReparent: (widget, parent) => {
      return false
    },
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      return frame.get();
    }
  }
}

/**
 * Generate Styles
 * 
*/
function getStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `${breakpoint}`);
  const deviceProperties = _.get(properties, `${breakpoint}`);
  const defaultStyles = _.get(defaultProperties, `regular.menuStyles`, {});

  //font-color
  const colorRgb = _.get(defaultProperties, `${active}.menuStyles.font.color`, null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }
  //background
  const backgroundRgb = _.get(defaultProperties, `${active}.menuStyles.background`, null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = getColorsConfig(backgroundRgb);
  }

  styles['background'] = background;

  const font = _.get(defaultStyles, 'font', {});

  //font-family
  const fontFamily = _.get(font, 'fontFamily');
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: fontFamily
    }
  }

  //font-size
  const fontSizeString = _.get(deviceProperties, 'regular.menuStyles.font.fontSize', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;

  //line spacing
  const lineSpacingString = _.get(deviceProperties, 'regular.menuStyles.font.lineSpacing', null);
  if (lineSpacingString) {
    const lineSpacing = parseFloat(lineSpacingString) + 'em';
    styles['lineHeight'] = lineSpacing;
  }

  //Letter Spacing
  const letterSpacingString = _.get(deviceProperties, 'regular.menuStyles.font.letterSpacing', null);
  if (letterSpacingString) {
    const letterSpacing = parseFloat(letterSpacingString) + 'em';
    styles['letterSpacing'] = letterSpacing;
  }

  //line spacing 
  const layoutHeight = _.get(deviceProperties, 'layout.height');
  if (layoutHeight && breakpoint === 'desktop') {
    styles['lineHeight'] = layoutHeight;
  }

  //font-style
  const bold = _.get(font, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = _.get(font, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = _.get(font, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //border
  const border = _.get(defaultStyles, 'border', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  // spacing 
  const spacing = _.get(deviceProperties, 'padding');
  const verticalPadding = _.get(deviceProperties, 'itemVerticalPadding');
  const horizontalPadding = _.get(deviceProperties, 'itemHorizontalPadding');

  styles = {
    ...styles,
    paddingLeft: `${horizontalPadding}px`,
    paddingRight: `${horizontalPadding}px`,
    paddingTop: `${verticalPadding}px`,
    paddingBottom: `${verticalPadding}px`
  }

  if (breakpoint === 'mobile') {
    styles = {
      ...styles,
      marginBottom: `${spacing}px`,
    }
  } else if (breakpoint === 'desktop') {
    styles = {
      ...styles,
      marginRight: `${spacing}px`,
    }
  }

  //alignment & padding
  const alignment = _.get(deviceProperties, 'alignment', {});
  var position = _.get(alignment, 'position', 'center');

  styles['justifyContent'] = position;
  styles['textAlign'] = position;

  var margin = _.get(alignment, 'margin', 0) || 0;
  if (position === 'left') {
    styles = {
      ...styles,
      paddingLeft: `${margin}px`
    }
  } else if (position === 'right') {
    styles = {
      ...styles,
      paddingRight: `${margin}px`
    }
  }

  return styles;
}

function getIconStyles(properties) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, 'mobile');
  const defaultStyles = _.get(defaultProperties, `regular.menuStyles`, {});

  //font-color
  const colorRgb = _.get(defaultStyles, `font.color`, null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //background
  const backgroundRgb = _.get(defaultStyles, `background`, null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = getColorsConfig(backgroundRgb);
  }

  styles['background'] = background;

  //border
  const border = _.get(defaultStyles, 'border', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  //alignment
  styles['justifyContent'] = 'center';
  styles['textAlign'] = 'center';

  return styles;

}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var styles = {};
  const topLeft = _.get(value, 'radius.topLeft', null);
  const topRight = _.get(value, 'radius.topRight', null);
  const bottomLeft = _.get(value, 'radius.bottomLeft', null);
  const bottomRight = _.get(value, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(value, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  styles['borderColor'] = color;

  const borderWidthString = _.get(value, 'width', null);
  var borderWidget = parseInt(borderWidthString) + 'px';

  styles['borderWidth'] = borderWidget;

  return styles;
}

MenuDraggableContainerWidget.processRequest = (widget) => {
  let content = _.get(widget, 'content');

  const updateContent = (menuContent) => {
    return menuContent.map(menuItem => {
      if (menuItem.action && menuItem.action.type === 'asset') {
        menuItem.action.value = _.get(menuItem ,'action.value.content.id') || _.get(menuItem, 'action.value');
      }
      if (menuItem.children && menuItem.children.length > 0) {
        menuItem.children = updateContent(menuItem.children);
      }
      
      //Process request for page action
      var actions = menuItem.action;
      var action = (actions) ? actions : null;
      if(action && action.type === 'page') {
        action = _.omit(action, 'page');

        if(action && action.value && 
          typeof action.value === 'object' && action.value.id) {
          action.value = action.value.id;
        }


        menuItem = { ...menuItem, action };
    }
      return menuItem;
    });
  };

  content = updateContent(content);

  return {
    ...widget,
    content: content
  }
}

export { MenuDraggableContainerWidget };